import { ElkNode } from 'elkjs/lib/elk.bundled.js';

import {
  CHILDREN_COUPLE_HANDLE_ID,
  CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID,
  CHILDREN_SPOUSE_1_EX_HANDLE_ID,
  CHILDREN_SPOUSE_1_HANDLE_ID,
  CHILDREN_SPOUSE_2_EX_COPARENT_HANDLE_ID,
  CHILDREN_SPOUSE_2_EX_HANDLE_ID,
  CHILDREN_SPOUSE_2_HANDLE_ID,
  EX_SPOUSE_CONNECTOR_WIDTH,
  EX_SPOUSE_WIDTH,
  MARRIAGE_GROUP_PREFIX,
  PERSON_NODE_WIDTH,
  SPOUSE_CONNECTOR_WIDTH,
} from './elkLayout.constants';

// Minimum pixel difference to consider parents meaningfully separated
const PARENT_POSITION_THRESHOLD = 10;

// Helper: Get sort order for source handle IDs (bottom handles, left to right)
const getSourceHandleOrder = (handle: string): number => {
  if (handle === CHILDREN_SPOUSE_1_EX_HANDLE_ID) return 0; // Leftmost
  if (handle === CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID) return 1; // Left co-parent
  if (handle === CHILDREN_SPOUSE_1_HANDLE_ID) return 2; // Spouse 1
  if (handle === CHILDREN_COUPLE_HANDLE_ID) return 3; // Center
  if (handle === CHILDREN_SPOUSE_2_HANDLE_ID) return 4; // Spouse 2
  if (handle === CHILDREN_SPOUSE_2_EX_COPARENT_HANDLE_ID) return 5; // Right co-parent
  if (handle === CHILDREN_SPOUSE_2_EX_HANDLE_ID) return 6; // Rightmost
  return 3; // Default to center
};

/**
 * Calculates the horizontal offset (from left edge) of a handle within a marriage node.
 * Marriage nodes have variable width based on ex-spouses:
 * [Ex-Spouse 1] [Connector] [Spouse 1] [Connector] [Spouse 2] [Connector] [Ex-Spouse 2]
 */
const getHandleOffsetInMarriageNode = (
  handle: string,
  nodeWidth: number,
  groupInfo?: MarriageGroupInfo
): number => {
  // If no group info, default to center
  if (!groupInfo) {
    return nodeWidth / 2;
  }

  const hasSpouse1Ex = groupInfo.spouse1ExSpouseIds.length > 0;
  const hasSpouse2Ex = groupInfo.spouse2ExSpouseIds.length > 0;
  const hasSpouse2 = !!groupInfo.spouse2Id;

  // Calculate actual component positions in the marriage node
  let currentOffset = 0;
  let spouse1ExEndOffset = 0;
  let spouse1StartOffset = 0;
  let spouse2StartOffset = 0;

  // Left ex-spouse section
  if (hasSpouse1Ex) {
    const spouse1ExWidth =
      groupInfo.spouse1ExSpouseIds.length * EX_SPOUSE_WIDTH;
    if (handle === CHILDREN_SPOUSE_1_EX_HANDLE_ID) {
      // Center of ex-spouse section
      return currentOffset + spouse1ExWidth / 2;
    }
    spouse1ExEndOffset = currentOffset + spouse1ExWidth;
    currentOffset += spouse1ExWidth + EX_SPOUSE_CONNECTOR_WIDTH;
  }

  // Spouse 1 position
  spouse1StartOffset = currentOffset;
  const spouse1Width = PERSON_NODE_WIDTH;

  // Individual spouse1 handle
  if (handle === CHILDREN_SPOUSE_1_HANDLE_ID) {
    return currentOffset + spouse1Width / 2;
  }

  // Co-parent handle between spouse1 ex and spouse1
  if (handle === CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID) {
    // Midpoint between ex-spouse center and spouse1 center
    const exCenter = spouse1ExEndOffset / 2; // Center of ex-spouse section
    const spouse1Center = spouse1StartOffset + spouse1Width / 2;
    return (exCenter + spouse1Center) / 2;
  }

  currentOffset += spouse1Width;

  // Couple connector and spouse2 (if exists)
  if (hasSpouse2) {
    currentOffset += SPOUSE_CONNECTOR_WIDTH;
    spouse2StartOffset = currentOffset;
    const spouse2Width = PERSON_NODE_WIDTH;

    // Individual spouse2 handle
    if (handle === CHILDREN_SPOUSE_2_HANDLE_ID) {
      return currentOffset + spouse2Width / 2;
    }

    // Couple handle (center between spouse1 and spouse2)
    if (handle === CHILDREN_COUPLE_HANDLE_ID) {
      return (
        (spouse1StartOffset +
          spouse1Width / 2 +
          spouse2StartOffset +
          spouse2Width / 2) /
        2
      );
    }

    currentOffset += spouse2Width;
  } else {
    // No spouse2, couple handle should be at spouse1 center
    if (handle === CHILDREN_COUPLE_HANDLE_ID) {
      return spouse1StartOffset + spouse1Width / 2;
    }
  }

  // Right ex-spouse section
  if (hasSpouse2Ex) {
    currentOffset += EX_SPOUSE_CONNECTOR_WIDTH;
    const spouse2ExWidth =
      groupInfo.spouse2ExSpouseIds.length * EX_SPOUSE_WIDTH;
    const spouse2ExCenter = currentOffset + spouse2ExWidth / 2;

    if (handle === CHILDREN_SPOUSE_2_EX_HANDLE_ID) {
      // Center of ex-spouse section
      return spouse2ExCenter;
    }

    // Co-parent handle between spouse2 and spouse2 ex
    if (handle === CHILDREN_SPOUSE_2_EX_COPARENT_HANDLE_ID) {
      // Midpoint between spouse2 center and ex-spouse center
      const spouse2Center = spouse2StartOffset + PERSON_NODE_WIDTH / 2;
      return (spouse2Center + spouse2ExCenter) / 2;
    }
  }

  // Default to node center
  return nodeWidth / 2;
};

interface MarriageGroupInfo {
  id: string;
  spouse1ExSpouseIds: string[];
  spouse2ExSpouseIds: string[];
  spouse2Id?: string | null;
}

interface ParentChildMaps {
  nodeToSourceHandle: Map<string, string>;
  nodeToParentIds: Map<string, Set<string>>;
}

/**
 * Builds maps of parent-child relationships from edges.
 * Tracks which source handle each child uses and all parent IDs.
 */
function buildParentChildMaps(
  edges: Array<{ sources?: string[]; targets?: string[] }>
): ParentChildMaps {
  const nodeToSourceHandle = new Map<string, string>();
  const nodeToParentIds = new Map<string, Set<string>>();

  edges.forEach((edge) => {
    const targetId = edge.targets?.[0]?.split('::')[0];
    const sourceId = edge.sources?.[0]?.split('::')[0];
    const sourceHandle = edge.sources?.[0]?.split('::')[1];

    if (targetId && sourceId && sourceHandle) {
      // Track source handle (prefer leftmost if multiple parents)
      const existing = nodeToSourceHandle.get(targetId);
      if (!existing) {
        nodeToSourceHandle.set(targetId, sourceHandle);
      } else if (
        getSourceHandleOrder(sourceHandle) < getSourceHandleOrder(existing)
      ) {
        nodeToSourceHandle.set(targetId, sourceHandle);
      }

      // Track ALL parent IDs
      if (!nodeToParentIds.has(targetId)) {
        nodeToParentIds.set(targetId, new Set());
      }
      nodeToParentIds.get(targetId)!.add(sourceId);
    }
  });

  return { nodeToSourceHandle, nodeToParentIds };
}

/**
 * Groups nodes by their Y position (layer).
 */
function groupNodesByLayer(nodes: ElkNode[]): Map<number, ElkNode[]> {
  const nodesByLayer = new Map<number, ElkNode[]>();

  nodes.forEach((node) => {
    const y = node.y ?? 0;
    if (!nodesByLayer.has(y)) {
      nodesByLayer.set(y, []);
    }
    nodesByLayer.get(y)!.push(node);
  });

  return nodesByLayer;
}

/**
 * Sorts nodes within a layer by:
 * 1. Average parent X position (keeps families together)
 * 2. Source handle (ex-spouse left, couple center, ex-spouse right)
 * 3. Age, youngest on the left (full siblings share parent + handle)
 * 4. Current X position
 */
function sortNodesByParentAndHandle(
  nodes: ElkNode[],
  nodeToParentIds: Map<string, Set<string>>,
  nodeToSourceHandle: Map<string, string>,
  nodeXPositions: Map<string, number>,
  childBirthTimestampByNodeId: Map<string, number | null>
): ElkNode[] {
  return [...nodes].sort((a, b) => {
    // 1. Primary sort: by average parent X position (keeps families together)
    const parentsA = nodeToParentIds.get(a.id);
    const parentsB = nodeToParentIds.get(b.id);

    if (parentsA && parentsB) {
      const avgParentXA =
        Array.from(parentsA).reduce(
          (sum, pid) => sum + (nodeXPositions.get(pid) ?? 0),
          0
        ) / parentsA.size;
      const avgParentXB =
        Array.from(parentsB).reduce(
          (sum, pid) => sum + (nodeXPositions.get(pid) ?? 0),
          0
        ) / parentsB.size;

      if (Math.abs(avgParentXA - avgParentXB) > PARENT_POSITION_THRESHOLD) {
        return avgParentXA - avgParentXB;
      }
    }

    // 2. Secondary sort: by source handle (ex-spouse left, couple center, ex-spouse right)
    const handleA = nodeToSourceHandle.get(a.id) || CHILDREN_COUPLE_HANDLE_ID;
    const handleB = nodeToSourceHandle.get(b.id) || CHILDREN_COUPLE_HANDLE_ID;

    const orderA = getSourceHandleOrder(handleA);
    const orderB = getSourceHandleOrder(handleB);

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // 3. Order full siblings by age, youngest on the left (T1-6540). At this
    // point the two nodes share parents and source handle, so a birth date is
    // the meaningful tiebreak. A later birth date means younger, so it sorts
    // first (leftmost); nodes without a date fall to the right of dated ones.
    const birthA = childBirthTimestampByNodeId.get(a.id) ?? null;
    const birthB = childBirthTimestampByNodeId.get(b.id) ?? null;

    if (birthA !== null && birthB !== null) {
      if (birthA !== birthB) return birthB - birthA;
    } else if (birthA !== null) {
      return -1;
    } else if (birthB !== null) {
      return 1;
    }

    // 4. Fallback to current x position
    return (a.x ?? 0) - (b.x ?? 0);
  });
}

/**
 * Groups children by their parent set AND source handle.
 * This keeps children from the same relationship together.
 */
function groupChildrenByParentAndHandle(
  nodes: ElkNode[],
  nodeToParentIds: Map<string, Set<string>>,
  nodeToSourceHandle: Map<string, string>
): Map<string, ElkNode[]> {
  const familyGroups = new Map<string, ElkNode[]>();

  nodes.forEach((node) => {
    const parents = nodeToParentIds.get(node.id);
    const handle = nodeToSourceHandle.get(node.id) || CHILDREN_COUPLE_HANDLE_ID;

    // Create a key that groups by parent(s) AND source handle
    const parentKey = parents ? Array.from(parents).sort().join('|') : 'orphan';
    const groupKey = `${parentKey}::${handle}`;

    if (!familyGroups.has(groupKey)) {
      familyGroups.set(groupKey, []);
    }
    familyGroups.get(groupKey)!.push(node);
  });

  return familyGroups;
}

interface CenteringParams {
  sortedNodes: ElkNode[];
  familyGroups: Map<string, ElkNode[]>;
  nodeToParentIds: Map<string, Set<string>>;
  nodeToSourceHandle: Map<string, string>;
  nodeXPositions: Map<string, number>;
  layoutResult: ElkNode;
  marriageGroups?: MarriageGroupInfo[];
}

/**
 * Centers each family group under their parent(s) while preventing overlaps.
 * Inspired by D3's tree layout algorithm for balanced family trees.
 */
function centerFamilyGroupsUnderParents(params: CenteringParams): void {
  const {
    sortedNodes,
    familyGroups,
    nodeToParentIds,
    nodeToSourceHandle,
    nodeXPositions,
    layoutResult,
    marriageGroups,
  } = params;

  // Create a map of marriage group info by ID for quick lookup
  const marriageGroupMap = new Map(marriageGroups?.map((g) => [g.id, g]) ?? []);

  const minSpacing = 32;
  const groupSpacing = 64; // Extra space between different family groups

  // Track all positioned node ranges for comprehensive collision detection
  const positionedRanges: Array<{ left: number; right: number }> = [];

  // Process groups in the order they appear in sortedNodes
  const processedNodes = new Set<string>();

  sortedNodes.forEach((node) => {
    if (processedNodes.has(node.id)) return;

    const parents = nodeToParentIds.get(node.id);
    const handle = nodeToSourceHandle.get(node.id) || CHILDREN_COUPLE_HANDLE_ID;
    const parentKey = parents ? Array.from(parents).sort().join('|') : 'orphan';
    const groupKey = `${parentKey}::${handle}`;
    const group = familyGroups.get(groupKey);

    if (!group) return;

    // Calculate parent center position based on the specific handle/port
    // For marriage groups with ex-spouses, we want to center children under their specific port
    let parentCenterX = 0;
    if (parents && parents.size > 0) {
      const parentPositions = Array.from(parents)
        .map((pid) => {
          const parentNode = layoutResult.children?.find((n) => n.id === pid);
          if (parentNode) {
            const nodeLeft = nodeXPositions.get(pid) ?? parentNode.x ?? 0;
            const nodeWidth = parentNode.width ?? 0;

            // For marriage groups, calculate center based on source handle
            // This positions children under the correct portion of the marriage node
            if (pid.startsWith(MARRIAGE_GROUP_PREFIX)) {
              const groupInfo = marriageGroupMap.get(pid);
              const handleOffset = getHandleOffsetInMarriageNode(
                handle,
                nodeWidth,
                groupInfo
              );
              return {
                left: nodeLeft + handleOffset,
                right: nodeLeft + handleOffset,
              };
            }

            // For regular person nodes, use full width
            return {
              left: nodeLeft,
              right: nodeLeft + nodeWidth,
            };
          }
          return null;
        })
        .filter((p): p is { left: number; right: number } => p !== null);

      if (parentPositions.length > 0) {
        const minX = Math.min(...parentPositions.map((p) => p.left));
        const maxX = Math.max(...parentPositions.map((p) => p.right));
        parentCenterX = (minX + maxX) / 2;
      }
    }

    // Calculate group width
    const groupWidth = group.reduce((sum, n, idx) => {
      return sum + (n.width ?? PERSON_NODE_WIDTH) + (idx > 0 ? minSpacing : 0);
    }, 0);

    // Calculate ideal start position (centered under parent handle)
    let startX = parentCenterX - groupWidth / 2;

    // Collision detection with restart mechanism
    // After shifting position, restart checking from the beginning to ensure
    // the new position doesn't collide with earlier ranges
    let hasCollision = true;
    let attempts = 0;
    const maxAttempts = positionedRanges.length + 1; // At most, we shift past each range once

    while (hasCollision && attempts < maxAttempts) {
      hasCollision = false;
      attempts++;

      for (const range of positionedRanges) {
        const proposedEndX = startX + groupWidth;

        // Check for overlap: group starts before range ends AND group ends after range starts
        if (
          startX < range.right + groupSpacing &&
          proposedEndX > range.left - groupSpacing
        ) {
          // Collision detected - shift right past this range and restart
          startX = range.right + groupSpacing;
          hasCollision = true;
          break; // Restart from beginning of positionedRanges
        }
      }
    }

    // Position each node in this group
    let xOffset = startX;
    const groupLeft = startX;
    group.forEach((n) => {
      n.x = xOffset;
      nodeXPositions.set(n.id, xOffset);
      processedNodes.add(n.id);
      xOffset += (n.width ?? PERSON_NODE_WIDTH) + minSpacing;
    });
    const groupRight = xOffset - minSpacing;

    // Record this group's range for future collision detection
    positionedRanges.push({
      left: groupLeft,
      right: groupRight,
    });
  });
}

/**
 * Main post-processing function that applies all transformations to the ELK layout.
 * This centers children under parents while respecting source handle ordering.
 */
export function applyFamilyTreePostProcessing(
  layoutResult: ElkNode,
  edges: Array<{ sources?: string[]; targets?: string[] }>,
  marriageGroups?: MarriageGroupInfo[],
  childBirthTimestampByNodeId: Map<string, number | null> = new Map()
): void {
  if (!layoutResult.children) return;

  // Step 1: Build parent-child relationship maps
  const { nodeToSourceHandle, nodeToParentIds } = buildParentChildMaps(edges);

  // Step 2: Group nodes by layer (Y position)
  const nodesByLayer = groupNodesByLayer(layoutResult.children);

  // Step 3: Build initial node X position map
  const nodeXPositions = new Map<string, number>();
  layoutResult.children.forEach((node) => {
    nodeXPositions.set(node.id, node.x ?? 0);
  });

  // Step 4: Process each layer from top to bottom
  const sortedLayerYs = Array.from(nodesByLayer.keys()).sort((a, b) => a - b);

  sortedLayerYs.forEach((layerY) => {
    const nodesInLayer = nodesByLayer.get(layerY)!;

    // Step 5: Sort nodes by parent position, source handle, then age
    const sortedNodes = sortNodesByParentAndHandle(
      nodesInLayer,
      nodeToParentIds,
      nodeToSourceHandle,
      nodeXPositions,
      childBirthTimestampByNodeId
    );

    // Step 6: Group children by parent and source handle
    const familyGroups = groupChildrenByParentAndHandle(
      sortedNodes,
      nodeToParentIds,
      nodeToSourceHandle
    );

    // Step 7: Center each family group under their parents
    centerFamilyGroupsUnderParents({
      sortedNodes,
      familyGroups,
      nodeToParentIds,
      nodeToSourceHandle,
      nodeXPositions,
      layoutResult,
      marriageGroups,
    });
  });
}
